import React, { useEffect, useState } from 'react';
import { startJourney, track, step as telemetryStep } from '../../telemetry/journey.js';
import { useParams, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { LuChevronLeft, LuMapPin } from 'react-icons/lu';
import { resolvePublicCharger } from '../../api/publicCharger.js';
import { useChargerBack } from '../../utils/chargerNav';
import {
  isChargerCode,
  isPublicChargerIdentifier,
} from '../../utils/chargerCode.js';
import { useOptionalSession } from '../../context/SessionContext.jsx';
import { useAuth } from '../../context/AuthContext.jsx';
import './ChargerEntry.less';

/**
 * /c/:identifier entry point.
 *
 * The single client-side entry for every QR-scan resolution. Accepts
 * either a canonical GCEV charger code OR a legacy internal
 * `stations.station_id` printed on pre-GCEV stickers. The value is
 * handed as-is to `GET /api/public/chargers/:identifier`, which
 * resolves legacy inputs to their canonical GCEV server-side and
 * applies one business-rule engine for both formats. The status
 * returned by the API drives the render:
 *
 *   - activated        -> redirects into the existing /station?stationId=<code> flow.
 *                         The router `state` (returnTo / source) is forwarded so the
 *                         standalone Back button on the wizard keeps its origin.
 *   - retired          -> friendly "no longer in service" page.
 *   - not_yet_active   -> friendly "not yet active" page.
 *   - inactive         -> same treatment as not_yet_active (previously bound + released).
 *   - network_removed  -> dedicated "not on the GreenCharge network" page. The
 *                         identifier is valid but the station has been
 *                         unpublished — GreenCharge no longer operates or
 *                         controls this charger. NOT the same as "not found",
 *                         NOT the same as "under maintenance", NOT the same as
 *                         a transient hardware fault. Terminal by business
 *                         rule: no retry, no polling, no session start.
 *   - 404              -> friendly "not found" page.
 *
 * Naming convention inside this file:
 *
 *   identifier          — the value the caller supplied on the URL. May
 *                         be a canonical GCEV or a legacy station id.
 *                         Sent as-is to the API.
 *   state.data.chargerCode — the CANONICAL GCEV returned by the API on
 *                         success (200). Even a legacy input resolves
 *                         to a canonical code by this point.
 *
 * Never throws to the user; surfaces a help message + the identifier
 * for support. Existing /station?stationId=<code> URLs are unaffected.
 */
const ChargerEntry = () => {
  const { identifier: raw } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { back, isPending } = useChargerBack();
  // Single source of truth for auth state — same AuthContext the rest
  // of the app reads. Anonymous visitors landing on a cold-scanned
  // charger have nowhere sensible to go "back" to inside the app, so
  // the control is omitted entirely rather than rendered-then-routed.
  // Suppress render while the context is still booting so an
  // authenticated user doesn't briefly see a missing button on cold
  // load, and an anonymous user doesn't briefly see one before it
  // disappears.
  const { isAuthenticated, isBooting: authBooting } = useAuth();
  const showBackButton = isAuthenticated && !authBooting;
  // Optional — the entry route is inside SessionProvider in prod, but
  // tests may render it in a bare MemoryRouter without the provider.
  const session = useOptionalSession();
  // Uppercase ONLY canonical GCEV codes so a legacy identifier's
  // original case is preserved — `stations.station_id` is TEXT and
  // case-sensitive at the DB level, so uppercasing `KTurSSgk` into
  // `KTURSSGK` would make the API lookup miss. GCEV codes are all-
  // uppercase by construction so the round-trip is a no-op for them.
  const decoded = decodeURIComponent(raw || '');
  const identifier = isChargerCode(decoded.toUpperCase())
    ? decoded.toUpperCase()
    : decoded;

  const [state, setState] = useState({ phase: 'loading', data: null, error: null });

  useEffect(() => {
    let alive = true;

    // Permissive shape check — this route now handles both canonical
    // GCEV codes and legacy internal station identifiers (base62
    // shorts, manufacturer serials, dev IDs). The API is the sole
    // authority on whether the value resolves; this guard only stops
    // empty / path-injecting payloads before we make a request.
    if (!isPublicChargerIdentifier(identifier)) {
      setState({ phase: 'invalid', data: null, error: null });
      return;
    }

    (async () => {
      try {
        // Journey telemetry: a QR scan starts (or resumes) the charging
        // journey BEFORE the lookup, so the lookup itself is part of it.
        startJourney({ entry: 'qr', stationId: identifier });
        track('charger.qr.scanned');
        telemetryStep('qr_landing');
        const data = await resolvePublicCharger(identifier);
        if (!alive) return;
        setState({ phase: 'resolved', data, error: null });
      } catch (err) {
        if (!alive) return;
        const status = err?.response?.status;
        if (status === 404) {
          setState({ phase: 'not_found', data: null, error: null });
        } else {
          setState({ phase: 'error', data: null, error: err?.message || 'Network error' });
        }
      }
    })();

    return () => { alive = false; };
  }, [identifier]);

  // When we know the scan resolved to a station that's off the network,
  // wipe any stale selected-station / pending-session context this device
  // may still hold from a previous scan of the same code. Prevents the
  // wizard from being reachable via a back-button or a Native rehydrate
  // pointing at data the customer must no longer be routed into. Safe
  // no-op when nothing's stored.
  const status = state.data?.status || null;
  useEffect(() => {
    if (status === 'network_removed' && session?.clearStation) {
      session.clearStation();
    }
  }, [status, session]);

  // Happy path — forward into the wizard. Preserve the router state so
  // the wizard's Back button on step 1 keeps its origin (returnTo / source).
  if (state.phase === 'resolved' && state.data?.chargeable) {
    return (
      <Navigate
        to={`/station?stationId=${encodeURIComponent(state.data.chargerCode)}`}
        state={location.state}
        replace
      />
    );
  }

  const isNetworkRemoved =
    state.phase === 'resolved' && state.data?.status === 'network_removed';

  return (
    <div className="charger-entry">
      <header className="charger-entry-header">
        {showBackButton && (
          <button
            type="button"
            className="charger-entry-back"
            onClick={back}
            disabled={isPending}
            aria-label="Back"
          >
            <LuChevronLeft aria-hidden="true" />
            <span>Back</span>
          </button>
        )}
      </header>

      <div className="charger-entry-card">
        <h1 className="brand">GreenCharge</h1>
        <p className="code"><span>Charger</span> <strong>{identifier || '—'}</strong></p>

        {state.phase === 'loading' && (
          <p className="status loading">Looking up charger…</p>
        )}

        {state.phase === 'invalid' && (
          <>
            <p className="status error-title">Invalid charger code</p>
            <p className="status-detail">
              This page expects a code like <code>GCEV1234567</code>. Check the sticker and try again.
            </p>
          </>
        )}

        {state.phase === 'not_found' && (
          <>
            <p className="status error-title">Charger not found</p>
            <p className="status-detail">
              We don't recognise this code. If the sticker is damaged, please contact support and
              quote the code above.
            </p>
          </>
        )}

        {state.phase === 'error' && (
          <>
            <p className="status error-title">Something went wrong</p>
            <p className="status-detail">{state.error}. Please try again in a moment.</p>
          </>
        )}

        {state.phase === 'resolved' && state.data?.status === 'retired' && (
          <>
            <p className="status retired-title">This charger is no longer in service</p>
            <p className="status-detail">
              The unit has been retired. Please find another GreenCharge station nearby.
            </p>
          </>
        )}

        {state.phase === 'resolved' && state.data?.status === 'not_yet_active' && (
          <>
            <p className="status pending-title">This charger isn't active yet</p>
            <p className="status-detail">
              The installer hasn't completed setup. If you're seeing this on a charger that should be
              live, please contact support and quote the code above.
            </p>
          </>
        )}

        {isNetworkRemoved && (
          <div className="network-removed" data-testid="charger-network-removed">
            <p className="status network-removed-title">
              This charger is not on the GreenCharge network
            </p>
            <p className="status-detail">
              GreenCharge does not currently operate or control this charger, so
              charging cannot be started through the app.
            </p>
            {state.data?.displayName && (
              <p className="network-removed-station">
                Scanned: <strong>{state.data.displayName}</strong>
                {state.data.locationText ? ` · ${state.data.locationText}` : ''}
              </p>
            )}
            <p className="status-detail">
              Please use the map to find another GreenCharge charger nearby.
            </p>

            <div className="charger-entry-actions">
              <button
                type="button"
                className="charger-entry-cta"
                onClick={() => navigate('/charge')}
              >
                <LuMapPin aria-hidden="true" />
                <span>Find another charger</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChargerEntry;
